import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { safeError } from '@/lib/safeError';

interface FileNode {
  path: string;
  name: string;
  type: 'component' | 'page' | 'util' | 'hook' | 'style' | 'api' | 'config' | 'test' | 'type' | 'asset' | 'unknown';
  exports: string[];
  imports: string[];
  size: number;
}

interface FileEdge {
  from: string;
  to: string;
  type: 'import' | 'export' | 'extends' | 'uses';
}

interface ComponentTreeNode {
  name: string;
  path: string;
  children: ComponentTreeNode[];
  type: 'component' | 'page' | 'layout';
}

interface ProjectGraph {
  nodes: FileNode[];
  edges: FileEdge[];
  componentTree: ComponentTreeNode[];
  routeMap: { route: string; file: string; type: 'page' | 'api' | 'layout' }[];
  dependencies: { name: string; version: string; type: 'dependency' | 'devDependency' }[];
  summary: {
    totalFiles: number;
    components: number;
    pages: number;
    hooks: number;
    utils: number;
    framework: 'react' | 'nextjs' | 'vite' | 'unknown';
  };
}

export type { FileNode, FileEdge, ComponentTreeNode, ProjectGraph };

export function useProjectGraph() {
  const [graph, setGraph] = useState<ProjectGraph | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateGraph = useCallback(async (
    projectId: string,
    files: { name: string; content: string }[]
  ): Promise<ProjectGraph | null> => {
    if (!projectId || files.length === 0) {
      setError('No files to analyze');
      return null;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-project-graph`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ projectId, files }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate project graph');
      }

      const result = await response.json();
      
      if (result.success && result.graph) {
        setGraph(result.graph);
        return result.graph;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      const e = safeError(err);
      setError(e.message);
      console.error('[useProjectGraph] Error:', e.name, e.message);
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const clearGraph = useCallback(() => {
    setGraph(null);
    setError(null);
  }, []);

  // Helper to get file info from graph
  const getFileInfo = useCallback((filePath: string): FileNode | undefined => {
    return graph?.nodes.find(n => n.path === filePath);
  }, [graph]);

  // Helper to get files that import a given file
  const getImporters = useCallback((filePath: string): string[] => {
    if (!graph) return [];
    return graph.edges
      .filter(e => e.to === filePath && e.type === 'import')
      .map(e => e.from);
  }, [graph]);

  // Helper to get files imported by a given file
  const getImports = useCallback((filePath: string): string[] => {
    if (!graph) return [];
    return graph.edges
      .filter(e => e.from === filePath && e.type === 'import')
      .map(e => e.to);
  }, [graph]);

  // Get a condensed graph description for AI context
  const getGraphContext = useCallback((): string => {
    if (!graph) return 'No project graph available.';

    const lines: string[] = [
      `## Project Structure (${graph.summary.framework} project)`,
      `- Total files: ${graph.summary.totalFiles}`,
      `- Components: ${graph.summary.components}`,
      `- Pages: ${graph.summary.pages}`,
      `- Hooks: ${graph.summary.hooks}`,
      `- Utilities: ${graph.summary.utils}`,
      '',
    ];

    // Component tree
    if (graph.componentTree.length > 0) {
      lines.push('## Component Tree');
      const renderTree = (nodes: ComponentTreeNode[], depth = 0): void => {
        for (const node of nodes.slice(0, 20)) { // Limit to avoid huge outputs
          const indent = '  '.repeat(depth);
          const icon = node.type === 'page' ? '📄' : '🧩';
          lines.push(`${indent}${icon} ${node.name} (${node.path})`);
          if (node.children.length > 0) {
            renderTree(node.children.slice(0, 5), depth + 1);
            if (node.children.length > 5) {
              lines.push(`${indent}  ... and ${node.children.length - 5} more`);
            }
          }
        }
      };
      renderTree(graph.componentTree);
      lines.push('');
    }

    // Route map
    if (graph.routeMap.length > 0) {
      lines.push('## Routes');
      for (const route of graph.routeMap.slice(0, 15)) {
        lines.push(`- ${route.route} → ${route.file} (${route.type})`);
      }
      if (graph.routeMap.length > 15) {
        lines.push(`... and ${graph.routeMap.length - 15} more routes`);
      }
      lines.push('');
    }

    // Key dependencies
    if (graph.dependencies.length > 0) {
      const keyDeps = graph.dependencies
        .filter(d => d.type === 'dependency')
        .slice(0, 10);
      if (keyDeps.length > 0) {
        lines.push('## Key Dependencies');
        for (const dep of keyDeps) {
          lines.push(`- ${dep.name}@${dep.version}`);
        }
        lines.push('');
      }
    }

    // File type breakdown
    lines.push('## Files by Type');
    const typeGroups = new Map<string, FileNode[]>();
    for (const node of graph.nodes) {
      const existing = typeGroups.get(node.type) || [];
      existing.push(node);
      typeGroups.set(node.type, existing);
    }
    for (const [type, files] of typeGroups) {
      if (files.length > 0) {
        lines.push(`### ${type.charAt(0).toUpperCase() + type.slice(1)}s (${files.length})`);
        for (const file of files.slice(0, 5)) {
          lines.push(`- ${file.path}`);
        }
        if (files.length > 5) {
          lines.push(`  ... and ${files.length - 5} more`);
        }
      }
    }

    return lines.join('\n');
  }, [graph]);

  return {
    graph,
    isGenerating,
    error,
    generateGraph,
    clearGraph,
    getFileInfo,
    getImporters,
    getImports,
    getGraphContext,
  };
}
